/**
 * Prospect follow-up sequences: cold-lead recovery and unsigned-consult recovery, ported from the
 * NorthOps "legal" pack (Law Firm (PI / Family): campaigns.cohorts + campaigns.sequences).
 *
 * DATA ONLY, DISABLED. LawBee has no engine that sends multi-step sequences to prospects (check-ins
 * work on open matters and send one generic nudge; the intake "missing information" email is a
 * single message a person triggers). Per the founder's rule for campaign config, these are ported
 * and kept off until such an engine exists: every sequence has `enabled: false` (typed as the
 * literal `false`) and nothing in LawBee reads this module to send anything.
 *
 * Guardrails any future engine must keep (encoded in `eligibility` and checked by tests):
 * - No legal advice: generic, administrative wording only; every email carries the
 *   not-legal-advice / no-lawyer-client-relationship notice and passes ai.screenClientText.
 * - Conflict check first: only prospects whose conflict check a lawyer recorded as `cleared`.
 *   Never contact someone with a recorded potential conflict.
 * - Consent: email only. Prospects (intakes, bookings) have no SMS opt-in in LawBee, so the pack's
 *   SMS steps became emails. Every message offers an easy way to stop.
 * - Variables: only `FOLLOW_UP_VARIABLES`, which LawBee can fill for a prospect.
 */

/** Placeholders a follow-up template may use; both are known for every intake and booking. */
export const FOLLOW_UP_VARIABLES = ['firm_name', 'client_first_name'] as const;

export const FOLLOW_UP_NOTICE =
  'This message is not legal advice and does not create a lawyer-client relationship.';

export type FollowUpStep = {
  channel: 'email';
  /** Hours to wait after the previous step (the first step: after the prospect becomes eligible). */
  delayHours: number;
  subject: string;
  body: string;
};

export type FollowUpSequence = {
  key: string;
  name: string;
  description: string;
  /** Always false until LawBee has a sequence engine (founder's rule: port campaigns, keep them off). */
  enabled: false;
  eligibility: {
    /** Who the sequence is for. */
    audience: 'unbooked_intake' | 'consult_not_retained';
    /** Days since the intake was submitted / the consultation took place. */
    ageDays: { min: number; max: number };
    /** Intake statuses that qualify (intake.IntakeStatus). */
    intakeStatuses: string[];
    /** A lawyer must have recorded this conflict decision before any message goes out. */
    conflictDecision: 'cleared';
    /** Stop as soon as any of these happen. */
    stopWhen: ('booked' | 'matter_opened' | 'declined' | 'replied' | 'unsubscribed' | 'potential_conflict')[];
  };
  maxTouches: number;
  throttlePerDay: number;
  /** Where this came from in the NorthOps legal pack. */
  source: { pack: 'legal'; cohort: string; sequence: string };
  steps: FollowUpStep[];
};

const STOP_LINE = 'If you no longer need our help, just reply "stop" and we will not contact you again.';

const email = (delayHours: number, subject: string, text: string): FollowUpStep => ({
  channel: 'email',
  delayHours,
  subject,
  body: `Hello {{client_first_name}},\n\n${text}\n\n${STOP_LINE}\n\n${FOLLOW_UP_NOTICE}\n\n{{firm_name}}`,
});

export const FOLLOW_UP_SEQUENCES: readonly FollowUpSequence[] = [
  {
    key: 'cold_lead_recovery',
    name: 'Cold-lead recovery',
    description: 'Follow up with people who sent an intake but never booked a consultation.',
    enabled: false,
    eligibility: {
      audience: 'unbooked_intake',
      ageDays: { min: 1, max: 14 },
      intakeStatuses: ['new', 'needs_info', 'in_review'],
      conflictDecision: 'cleared',
      stopWhen: ['booked', 'matter_opened', 'declined', 'replied', 'unsubscribed', 'potential_conflict'],
    },
    maxTouches: 2,
    throttlePerDay: 15,
    source: { pack: 'legal', cohort: 'unresponsive_leads', sequence: 'unresponsive_leads_seq' },
    steps: [
      email(
        2,
        'Following up on your inquiry with {{firm_name}}',
        'Thank you for reaching out to {{firm_name}}. We would be glad to set up a consultation with one of our lawyers. Reply to this email with a few times that suit you, or call our office.',
      ),
      email(
        24,
        'Still happy to help, {{client_first_name}}',
        'We are following up on your inquiry. If you would still like to speak with a lawyer, reply to this email and we will find a time that works for you.',
      ),
    ],
  },
  {
    key: 'unsigned_consult_recovery',
    name: 'Unsigned-consult recovery',
    description: 'Check in with people who had a consultation but have not retained the firm yet.',
    enabled: false,
    eligibility: {
      audience: 'consult_not_retained',
      ageDays: { min: 4, max: 30 },
      intakeStatuses: ['new', 'in_review'],
      conflictDecision: 'cleared',
      stopWhen: ['matter_opened', 'declined', 'replied', 'unsubscribed', 'potential_conflict'],
    },
    maxTouches: 3,
    throttlePerDay: 15,
    source: { pack: 'legal', cohort: 'consults_not_signed', sequence: 'consults_not_signed_seq' },
    steps: [
      email(
        24,
        'Thank you for meeting with {{firm_name}}',
        'Thank you for your consultation with {{firm_name}}. We are here whenever you are ready to move forward.',
      ),
      email(
        48,
        'Any questions after your consultation?',
        'We are following up after your consultation. If you have questions about working with us, reply to this email or call our office and we will be glad to help.',
      ),
      email(
        120,
        'Checking in from {{firm_name}}',
        'We are checking in one last time. Whenever you are ready, we are happy to help.',
      ),
    ],
  },
];
